import { useState } from "react";
import { Paperclip, CheckCircle2, Clock, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import api from "../../lib/api";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

export const SectionSelect = ({ sections, value, onChange, testId, className = "" }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`h-9 px-3 text-sm bg-card hairline rounded-none focus:outline-none focus:ring-1 focus:ring-[#1E3A5F] ${className}`}
    data-testid={testId}
  >
    {Object.entries(sections).map(([k, label]) => (
      <option key={k} value={k}>{label}</option>
    ))}
  </select>
);

export const downloadBlob = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename || "archivo";
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 2000);
  } catch {
    toast.error(t("Error descargando el archivo"));
  }
};

export const AskQuestionBox = ({ companyId, sections, onAsked }) => {
  const [text, setText] = useState("");
  const [section, setSection] = useState("otros");
  const [sending, setSending] = useState(false);

  const ask = async () => {
    if (text.trim().length < 10) return toast.error(t("Escribe una pregunta de al menos 10 caracteres"));
    setSending(true);
    try {
      await api.post(`/companies/${companyId}/questions`, { question_text: text.trim(), section });
      setText("");
      toast.success(t("Pregunta enviada al vendedor"));
      onAsked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando la pregunta");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="hairline bg-card p-5 space-y-3" data-testid="dataroom-ask-box">
      <span className="font-mono-label text-[#E0712C]">{t("Nueva pregunta al vendedor")}</span>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="rounded-none"
        placeholder={t("Formula tu pregunta. La respuesta será visible, de forma anónima, para todos los compradores con NDA aprobado.")}
        data-testid="dataroom-ask-input" />
      <div className="flex flex-wrap items-center gap-3">
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="dataroom-ask-section" />
        <Button onClick={ask} disabled={sending} className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-9 px-5" data-testid="dataroom-ask-btn">
          <Send className="w-3 h-3 mr-2" /> {sending ? t("Enviando…") : t("Enviar pregunta")}
        </Button>
      </div>
    </div>
  );
};

export const QuestionItem = ({ q, isOwner, sections, onChanged, mine = false }) => {
  const [answer, setAnswer] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const reclassify = async (section) => {
    try {
      await api.patch(`/questions/${q.question_id}/section`, { section });
      onChanged();
    } catch {
      toast.error(t("No se pudo cambiar la sección"));
    }
  };

  const send = async () => {
    if (answer.trim().length < 5) return toast.error(t("Escribe una respuesta de al menos 5 caracteres"));
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("answer_text", answer.trim());
      if (file) fd.append("file", file);
      await api.post(`/questions/${q.question_id}/answer`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Respuesta publicada en el Data Room"));
      setAnswer("");
      setFile(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando la respuesta");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-6 py-5" data-testid={`dr-question-${q.question_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-sm leading-relaxed">{q.question_text}</p>
          <p className="font-mono-label text-muted-foreground mt-1.5" style={{ fontSize: "0.6rem" }}>
            {isOwner ? `${q.asker_name} · ${q.asker_role === "investor" ? t("Inversor") : t("Searcher")} · ` : mine ? t("Tu pregunta ·") : t("Pregunta de un comprador ·")}
            {new Date(q.created_at).toLocaleDateString("es-ES")}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {isOwner && <SectionSelect sections={sections} value={q.section} onChange={reclassify} testId={`dr-section-${q.question_id}`} />}
          {q.status === "answered" ? (
            <span className="font-mono-label text-forest inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{" "}{t("Respondida")}</span>
          ) : (
            <span className="font-mono-label text-[#E0712C] inline-flex items-center gap-1"><Clock className="w-3 h-3" />{" "}{t("Pendiente")}</span>
          )}
        </div>
      </div>
      {q.answer_text && (
        <div className="mt-3 hairline p-4 bg-bone">
          <span className="font-mono-label text-gold">{t("Respuesta del vendedor")}</span>
          <p className="text-sm mt-1 leading-relaxed whitespace-pre-line text-muted-foreground">{q.answer_text}</p>
          {q.attachment && (
            <button type="button" onClick={() => downloadBlob(`/questions/${q.question_id}/attachment`, q.attachment.filename)}
              className="font-mono-label text-gold mt-3 inline-flex items-center gap-1 hover:opacity-70" data-testid={`dr-attachment-${q.question_id}`}>
              <Paperclip className="w-3 h-3" /> {q.attachment.filename}
            </button>
          )}
        </div>
      )}
      {isOwner && q.status !== "answered" && (
        <div className="mt-3 space-y-2">
          <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} className="rounded-none"
            placeholder={t("Escribe tu respuesta. Será visible, de forma anónima respecto a quien preguntó, para todos los compradores con NDA aprobado.")}
            data-testid={`dr-answer-input-${q.question_id}`} />
          <div className="flex flex-wrap items-center gap-3">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-none h-9 max-w-xs text-xs" data-testid={`dr-answer-file-${q.question_id}`} />
            <Button onClick={send} disabled={sending} className="rounded-none h-9 px-5 text-bone" style={{ backgroundColor: "#2E6F80" }} data-testid={`dr-answer-btn-${q.question_id}`}>
              {sending ? t("Publicando…") : t("Publicar respuesta")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
